import { SPBox, SPButton, SPModal } from '@group-link-one/gl-ui/sonarprint';
import React, { createContext, useCallback, useState } from 'react';
import { useModalLogoutTranslation } from './hooks/useModalLogoutTranslation';
import type { ModalLogoutContextProps, ModalLogoutProviderProps } from './ModalLogoutContext.types';

export const ModalLogoutContext = createContext({} as ModalLogoutContextProps);

export function ModalLogoutProvider({
  children,
  callbackFnToModalLogout,
}: ModalLogoutProviderProps) {
  const [modalLogoutIsOpenOpen, setModalLogoutIsOpenOpen] = useState<boolean>(false);
  const { t } = useModalLogoutTranslation();

  const handleOpenModalLogout = useCallback(() => {
    if (modalLogoutIsOpenOpen) return;
    setModalLogoutIsOpenOpen(true);
  }, [modalLogoutIsOpenOpen]);

  const handleCloseModalLogout = useCallback(() => {
    setModalLogoutIsOpenOpen(false);
  }, []);

  const handleClickOnOk = useCallback(() => {
    handleCloseModalLogout();
    callbackFnToModalLogout();
  }, [handleCloseModalLogout, callbackFnToModalLogout]);

  return (
    <ModalLogoutContext.Provider
      value={{
        modalLogoutIsOpenOpen,
        handleOpenModalLogout,
        handleCloseModalLogout,
      }}
    >
      {children}

      <SPModal
        open={modalLogoutIsOpenOpen}
        title={t('title')}
        description={t('description')}
        content={
          <SPBox display='flex' justifyContent='flex-end' width='100%' gap='16px'>
            <SPButton
              variant='secondary'
              text={t('buttonText')}
              maxWidth='150px'
              onClick={handleClickOnOk}
              data-testid='modal-logout-cancel-button'
            />
          </SPBox>
        }
      />
    </ModalLogoutContext.Provider>
  );
}

export const useModalLogout = () => {
  const context = React.useContext(ModalLogoutContext);
  if (context === undefined) {
    throw new Error('useModalLogout must be used within a ModalLogoutProvider');
  }
  return context;
};
